import * as React from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { List, RowComponentProps } from "react-window";
import Typography from "@mui/material/Typography";
import { Box, Paper, createFilterOptions, Skeleton } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SearchIcon from "@mui/icons-material/Search";
import { ContentItemProps, TARGET_ERRORS } from "../constants";
import { InputAdornment } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { debounce } from "lodash";
export const ListOption: React.FC<
// `data-cy` is declared rather than left to the `...props` spread: it is set
// explicitly by the caller, and JSX excess-property checking rejects an
// attribute a component's props type does not name. The same reasoning
// covers the fields below, which MUI's `renderValue` `getItemProps()`
// spreads onto the rendered value node (see SearchField's `renderValue`).
Omit<ContentItemProps, "onDelete"> & {
  isListItem?: boolean;
  "data-cy"?: string;
  className?: string;
  disabled?: boolean;
  "data-item-index"?: number;
  tabIndex?: number;
  onDelete?: (event?: unknown) => void;
}> = ({
  label,
  path,
  ZUID,
  langCode,
  isPublished,
  isListItem = true,
  type,
  onDelete = () => {},
  ...props
}) => {
  return <Box key={ZUID} {...props} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" flexGrow={1} columnGap="12px" {...isListItem ? {
    px: "16px",
    py: "8px"
  } : {
    pl: "8px",
    width: "100%",
    height: "52px"
  }} data-sentry-element="Box" data-sentry-component="ListOption" data-sentry-source-file="SearchField.tsx">
      {type === "pageset" ? <DescriptionIcon fontSize="small" color="action" /> : <FormatListBulletedIcon fontSize="small" color="action" />}
      <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="stretch" flexGrow={1} sx={{
      overflow: "hidden",
      position: "relative",
      boxSizing: "border-box"
    }} data-sentry-element="Box" data-sentry-source-file="SearchField.tsx">
        <Typography variant="body2" color="text.primary" noWrap textOverflow="ellipsis" overflow="hidden" fontWeight={500} px="2px" data-sentry-element="Typography" data-sentry-source-file="SearchField.tsx">
          {`(${langCode}) ${label?.trim()}`}
        </Typography>
        <Typography variant="body2" color="info.dark" noWrap textOverflow="ellipsis" maxWidth="100%" overflow="hidden" px="2px" data-sentry-element="Typography" data-sentry-source-file="SearchField.tsx">
          {path}
        </Typography>
      </Box>
    </Box>;
};
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
  const {
    children,
    ...other
  } = props;
  const items = React.Children.toArray(children);
  const rowHeight = 56;
  const Row = ({
    index,
    style,
    data
  }: any) => {
    return <li style={style} data-sentry-component="Row" data-sentry-source-file="SearchField.tsx">{data[index]}</li>;
  };
  return <div ref={ref} data-cy="RedirectsTargetOptionsContainer" style={{
    width: "100%",
    height: `${rowHeight * 5}px`
  }} {...other}>
      <ul>
        <List rowCount={items.length} rowHeight={rowHeight} overscanCount={5} rowProps={{
        data: items
      }} rowComponent={Row} />
      </ul>
    </div>;
});
type SearchFieldProps = {
  options: ContentItemProps[];
  loading?: boolean;
  value: ContentItemProps;
  defaultValue?: string;
  onChange: (value: ContentItemProps) => void;
  readOnly?: boolean;
  onSearch?: (term: string) => void;
  // Test-hook prefix. Defaults to the redirects naming so existing specs and
  // both original call sites keep the exact selectors they had.
  dataCy?: string;
  // The unpublished-item warning is redirect-specific ("incoming paths will
  // lead to your 404 page"), so callers outside redirects opt out of it.
  hideUnpublishedWarning?: boolean;
};
const SearchField: React.FC<SearchFieldProps> = ({
  options,
  loading = false,
  value,
  defaultValue,
  onChange,
  readOnly = false,
  onSearch,
  dataCy = "RedirectsSearchField",
  hideUnpublishedWarning = false
}) => {
  const {
    t
  } = useTranslation();
  const textInputRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const defaultFilter = createFilterOptions<ContentItemProps>({
    matchFrom: "any",
    stringify: (option: ContentItemProps) => `${option?.label}\n${option?.path}\n${option?.ZUID}`
  });
  const filterOptions = (opts: ContentItemProps[], state: Parameters<typeof defaultFilter>[1]) => {
    const filtered = defaultFilter(opts, state);

    /*
      Limit the rendered items to 100 items max to avoid performance issues
      and require the user to type more to narrow down results
     */
    return filtered.slice(0, 100);
  };
  const handleDebouncedInput = React.useCallback(debounce((value: string) => {
    if (value && onSearch) {
      onSearch(value);
    }
  }, 500), []);
  React.useEffect(() => {
    if (!defaultValue) return;
    if (!loading && !!options) {
      const foundValue = options?.find(item => item?.ZUID === defaultValue);
      onChange(foundValue);
    }
  }, [defaultValue, options, loading]);
  return <>
      {loading && !!defaultValue ? <Paper elevation={0} variant="outlined" sx={{
      px: "12px",
      py: "8px",
      width: "100%",
      height: "64px",
      borderRadius: "8px",
      borderColor: "border"
    }}>
          <ListOptionSkeleton count={1} />
        </Paper> : <Autocomplete data-cy={`${dataCy}Input`} readOnly={readOnly} disabled={readOnly} open={readOnly ? false : open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} limitTags={1} options={loading ? [] : options} size="small" fullWidth loading={loading} loadingText={<ListOptionSkeleton count={4} />} value={value} filterOptions={filterOptions} onInputChange={(evt, value) => handleDebouncedInput(value)} onClickCapture={e => {
      if (!!value) {
        setOpen(true);
      }
    }} noOptionsText={t("shell.noResultsFound")} renderInput={params => {
      return <TextField {...params} data-cy={`${dataCy}InputField`} inputRef={textInputRef} slotProps={{
        input: {
          ...params.InputProps,
          placeholder: t("seo.searchForItem"),
          readOnly: !!value,
          ...(!!value ? {} : {
            startAdornment: <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
          })
        }
      }} sx={{
        "& .MuiInputBase-root .MuiAutocomplete-endAdornment": {
          right: "16px!important"
        },
        "& .MuiInputBase-root": {
          paddingRight: "56px!important"
        }
      }} />;
    }} renderValue={(data: ContentItemProps | null, getItemProps) => <ListOption {...getItemProps()} data-cy={`${dataCy}Value`} label={data?.label} path={data?.path} ZUID={data?.ZUID} langCode={data?.langCode} isPublished={data?.isPublished} type={data?.type} isListItem={false} />} onChange={(_: any, newValue: ContentItemProps | null) => onChange(newValue)} renderOption={(props, option) => <ListOption {...props} key={option.ZUID}
    // Keyed by ZUID so a spec can click one specific result instead
    // of matching on its label — the label is user copy and the list
    // is sorted by createdAt, so neither is a stable handle.
    data-cy={`${dataCy}Option-${option.ZUID}`} label={option.label} path={option.path} ZUID={option.ZUID} langCode={option.langCode} isPublished={option.isPublished} type={option.type} />} slotProps={{
      listbox: {
        component: ListboxComponent
      }
    }} sx={{
      "& .MuiInputBase-root input": {
        width: !!value ? "5px" : "auto",
        minWidth: "0!important"
      },
      "& .MuiAutocomplete-popupIndicator": {
        display: "none"
      }
    }} />}

      {value && !value?.isPublished && !hideUnpublishedWarning && <Typography variant="body2" color="warning.dark" mt="4px" data-cy={`${dataCy}Error`}>
          {t(TARGET_ERRORS.unpublished)}
        </Typography>}
    </>;
};
export const ListOptionSkeleton = ({
  count = 4
}: {
  count: number;
}) => {
  return <>
      {Array.from({
      length: count
    }).map((_, index) => <Box data-cy="RedirectsTargetListLoadingSkeleton" key={index} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%" columnGap="12px" py="8px">
          <Skeleton variant="circular" sx={{
        width: "20px",
        height: "20px",
        flexShrink: 0
      }} />

          <Box display="flex" flexDirection="column" justifyContent="cennter" alignItems="flex-start" width="100%" flexGrow={1} rowGap="6px">
            <Skeleton width="45%" height="12px" variant="rounded" />
            <Skeleton width="90%" height="12px" variant="rounded" />
          </Box>
        </Box>)}
    </>;
};
export default SearchField;